import { useMemo, useState } from "react";
import isEqual from "lodash/isEqual";

import BaseDialog from "@/components/Modals/BaseDialog";
import { DeleteDialog } from "@/components/Modals/DeleteDialog";
import { NoxusInput } from "@/components/Form/InputField";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { ErrorMessage } from "@/components/Form/ErrorMessage";
import { CodeEditorSurface } from "@/components/Form/CodeEditor/CodeEditor";
import AITiptapEditor from "@/components/Form/TiptapEditor/AITiptapEditor";
import { getTemplateStringValue } from "@/components/Form/RichTextField";
import { Separator } from "@/components/shadcn/separator";
import type { SkillConfig } from "@/generated";
import { SKILL_MD } from "./skillMd";
import {
  addFile,
  renameFile,
  deleteFile,
  fileLanguage,
  getFileContent,
  isMarkdownFile,
  setFileContent,
  usageConditionsError,
  validateSkill,
} from "./skillDraft";
import { SkillFileRail } from "./SkillFileRail";

const IMPROVER_CONTEXT =
  "A skill: a markdown instruction package an agent loads on demand to carry " +
  "out a specific kind of task.";

const NAME_TOOLTIP = "Read by the agent: it loads the skill by this name.";

export const SkillEditorDialog = ({
  skill,
  otherNames,
  originalName,
  isOpen,
  onOpenChange,
  onSave,
  isDisabled,
}: {
  skill: SkillConfig;
  otherNames: string[];
  /** The name this skill already had, or undefined when it is being created.
   *  A name the user has not touched is accepted as-is; a new skill is not,
   *  or two skills left on the default name would both save. */
  originalName?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (skill: SkillConfig) => void;
  isDisabled?: boolean;
}) => {
  const [draft, setDraft] = useState(skill);
  const [selected, setSelected] = useState(SKILL_MD);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

  const nameError = validateSkill(draft, otherNames, originalName);
  const usageConditionsProblem = usageConditionsError(draft);

  const content = getFileContent(draft, selected);
  const isDirty = useMemo(() => !isEqual(draft, skill), [draft, skill]);
  // Save is held until the name is usable, so the reason has to be on screen by
  // then — but not before the user has touched anything.
  const showNameError = !!nameError && isDirty;

  const handleSave = () => {
    if (nameError || usageConditionsProblem) return;
    onSave({
      ...draft,
      name: draft.name.trim(),
      usage_conditions: (draft.usage_conditions ?? "").trim(),
    });
    onOpenChange(false);
  };

  // The X, Escape and a backdrop click all land here. Closing with unsaved
  // work would discard a whole document silently, so it asks first.
  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (isDirty) {
      setIsConfirmingDiscard(true);
      return;
    }
    onOpenChange(false);
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      // Radix would close the editor on Escape before the guard below runs.
      onEscapeKeyDown={(event) => {
        event.preventDefault();
        if (!isDirty) {
          onOpenChange(false);
          return;
        }
        // Deferred past this keypress on purpose: Radix dispatches Escape
        // through its layer stack, and a dialog mounted during that dispatch is
        // dismissed by the same event -- the prompt appeared and vanished.
        requestAnimationFrame(() => setIsConfirmingDiscard(true));
      }}
      title={originalName === undefined ? "New skill" : "Edit skill"}
      size="6xl"
      footerActions={[
        {
          label: "Cancel",
          props: { variant: "outline", onClick: () => handleOpenChange(false) },
        },
        {
          label: "Save",
          props: {
            onClick: handleSave,
            isDisabled:
              isDisabled || !isDirty || !!nameError || !!usageConditionsProblem,
          },
        },
      ]}
    >
      {/* A viewport height, not h-full: the dialog sizes to its content, so a
          percentage chain never becomes definite and the editor could not tell
          how tall it was allowed to be — the document grew the dialog instead
          of scrolling inside its pane. */}
      <div className="flex h-[70vh] min-h-0 flex-col gap-3">
        <div>
          <NoxusInput
            label="Name"
            tooltip={NAME_TOOLTIP}
            isRequired
            value={draft.name}
            placeholder="e.g. code-review"
            disable={isDisabled}
            hasError={showNameError}
            onChange={(event) =>
              setDraft((d) => ({ ...d, name: event.target.value }))
            }
          />
          {showNameError && <ErrorMessage errorMessage={nameError} />}
        </div>

        <div>
          <NoxusFormLabel
            label="Usage conditions"
            name="usage_conditions"
            isRequired
            isDisabled={isDisabled}
            tooltip="Read by the agent, not by your team. It is the only thing the agent sees about this skill when it decides whether to load it."
          />
          <AITiptapEditor
            value={[getTemplateStringValue(draft.usage_conditions ?? "")]}
            onChange={(next) => {
              const latest = next[next.length - 1];
              if (latest) {
                setDraft((d) => ({ ...d, usage_conditions: latest.text }));
              }
            }}
            isControlled
            placeholder="e.g. Use when reviewing a pull request or a diff. Not for writing new code."
            isDisabled={isDisabled}
            improverContext={IMPROVER_CONTEXT}
          />
          {usageConditionsProblem && isDirty && (
            <ErrorMessage errorMessage={usageConditionsProblem} />
          )}
        </div>

        <Separator />

        {/* The implicit grid row is `auto`, which sizes to content — the columns
            would never get a definite height for the editor to scroll within. */}
        <div className="grid min-h-0 flex-1 grid-cols-5 grid-rows-[minmax(0,1fr)] gap-3">
          <div className="col-span-2 flex min-h-0 min-w-0 flex-col">
            <SkillFileRail
              skill={draft}
              selected={selected}
              isDisabled={isDisabled}
              onSelect={setSelected}
              onAdd={(fileName) => setDraft((d) => addFile(d, fileName))}
              onRename={(from, to) => {
                setDraft((d) => renameFile(d, from, to));
                if (selected === from) setSelected(to);
              }}
              onDelete={(fileName) => {
                setDraft((d) => deleteFile(d, fileName));
                if (selected === fileName) setSelected(SKILL_MD);
              }}
            />
          </div>

          {/* -m-1/p-1: the overflow box would otherwise clip the editor's own
              focus ring, and the padding is taken back off the layout so the
              pane still lines up with the rail. */}
          <div className="col-span-3 -m-1 flex min-h-0 min-w-0 flex-col overflow-hidden p-1">
            {isMarkdownFile(selected) ? (
              <AITiptapEditor
                key={selected}
                value={[getTemplateStringValue(content)]}
                // Functional update: the editor debounces, so a positional
                // update would clobber metadata typed in the meantime.
                onChange={(next) => {
                  const latest = next[next.length - 1];
                  if (latest) {
                    setDraft((d) => setFileContent(d, selected, latest.text));
                  }
                }}
                isControlled
                fullSize
                label={selected}
                placeholder={
                  selected === SKILL_MD
                    ? "Markdown instructions the agent loads when it uses this skill…"
                    : "Markdown content…"
                }
                isDisabled={isDisabled}
                improverContext={IMPROVER_CONTEXT}
              />
            ) : (
              <CodeEditorSurface
                key={selected}
                value={content}
                onChange={(next) =>
                  setDraft((d) => setFileContent(d, selected, next))
                }
                inputs={[]}
                language={fileLanguage(selected)}
                placeholder="File content…"
                isDisabled={isDisabled}
                fillHeight
              />
            )}
          </div>
        </div>
      </div>

      <DeleteDialog
        isOpen={isConfirmingDiscard}
        onOpenChange={setIsConfirmingDiscard}
        onDelete={() => {
          setIsConfirmingDiscard(false);
          onOpenChange(false);
        }}
        title="Discard changes to this skill?"
        text="Your edits have not been saved and will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
      />
    </BaseDialog>
  );
};
